import React, { Fragment, useEffect, useState } from "react";

import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { IMaskInput } from "react-imask";

import Button from "@/components/Button";
import Dropdown from "@/components/Dropdown";
import InputPills from "@/components/InputPills";
import InputRadio from "@/components/InputRadio";
import useLocale from "@/hooks/useLocale";
import { submitActivation } from "@/utils/activation";
import { submitAnswer } from "@/utils/answers";
import { type Field, FieldType, type PublicFormInstance } from "@/utils/form";
import { postalCodeMask } from "@/utils/masks";
import { getRulesAndRegulationsFileUrl } from "@/utils/rulesAndRegulations";
import { getUserByEmail } from "@/utils/search";

import type { Option } from "@/components/Dropdown";

const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const INPUT_CLASS =
  "w-full py-2 px-5 h-[50px] placeholder:text-blue placeholder:font-semibold placeholder:text-[14px] text-blue rounded-[100px] bg-[#F0F0F0]";

// Make every anchor in server-provided HTML open in a new tab, safely.
// html may be missing (e.g. a locale not configured on the field label),
// so guard against undefined/null instead of throwing.
const openLinksInNewTab = (html?: string | null): string =>
  html
    ? html.replace(
        /<a\b(?![^>]*\btarget=)/gi,
        '<a target="_blank" rel="noopener noreferrer"',
      )
    : "";

interface FormViewProps {
  formInstance: PublicFormInstance | null;
  onSuccess: (message?: string) => void;
  onError: (error: { title: string; message: string }) => void;
}

const FormView: React.FC<FormViewProps> = ({
  formInstance,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const locale = useLocale();

  const [formData, setFormData] = useState<{
    email: string;
    answers: Record<string, string | number | boolean>;
  }>({
    email: "",
    answers: {},
  });
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const onInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: Field,
  ) => {
    const { value, checked } = e.target as HTMLInputElement;

    if (field?.type === FieldType.MARKETING) {
      // Ford expects marketing opt-in as "A" (opted in) / "H" (declined),
      // not a raw boolean — the export drops rows with an invalid value.
      setFormData((prev) => ({
        ...prev,
        answers: {
          ...prev.answers,
          [field.key]: checked ? "A" : "H",
        },
      }));
      return;
    }

    if (field?.type === FieldType.PHONE) {
      // Ford expects a bare 10-digit phone number: numeric only, no spaces or
      // punctuation. Strip everything but digits and cap at 10.
      setFormData((prev) => ({
        ...prev,
        answers: {
          ...prev.answers,
          [field.key]: value.replace(/\D/g, "").slice(0, 10),
        },
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [field.key]: value,
      },
    }));
  };

  // react-imask calls this with the unmasked value; store it against the field's key.
  const onPostalCodeAccept = (unmaskedValue: string, field: Field) => {
    setFormData((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [field.key]: unmaskedValue.toUpperCase(),
      },
    }));
  };

  const handleSubmitActivation = async () => {
    let foundEntry = null;
    try {
      const searchResult = await getUserByEmail(formData.email);
      if (searchResult) {
        foundEntry = {
          ...searchResult,
          instance_key: import.meta.env.VITE_FORM_INSTANCE_KEY,
        };
      }
    } catch (err) {
      // A real lookup failure (network/server error), not a "not found" —
      // don't silently fall through to creating a duplicate activation.
      console.log("Error looking up existing activation:", err);
      Sentry.captureException(err);
      setIsFormSubmitting(false);
      onError({
        title: "Could not submit activation form.",
        message: (err as Error).message,
      });
      return false;
    }

    if (foundEntry) {
      // A799E is already stored as "A"/"H"; default to "H" when unchecked/unset.
      const newAnswerValue = formData.answers.A799E === "A" ? "A" : "H";
      const marketingOptInAnswer = foundEntry.answers.find(
        (a: { field_key: string; value: string }) => a.field_key === "A799E",
      );
      if (
        !marketingOptInAnswer ||
        marketingOptInAnswer.value !== newAnswerValue
      ) {
        await submitAnswer({
          instance_key: foundEntry.instance_key,
          email: formData.email,
          field_key: "A799E",
          value: newAnswerValue,
        });
      }
    } else {
      try {
        await submitActivation({
          email: formData.email,
          answers: formData.answers,
          locale: locale,
        });
      } catch (err) {
        console.log("Error submitting activation form:", err);
        Sentry.captureException(err);
        setIsFormSubmitting(false);
        onError({
          title: "Could not submit activation form.",
          message: (err as Error).message,
        });
        return false;
      }
    }

    return true;
  };

  const onFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsFormSubmitting(true);

    try {
      // Only show the success view if submission actually went through —
      // handleSubmitActivation reports handled failures via its return value.
      const submitted = await handleSubmitActivation();
      if (submitted) {
        onSuccess();
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      Sentry.captureException(err);
      setIsFormSubmitting(false);
      onError({
        title: "Could not submit form.",
        message: (err as Error).message,
      });
    }
  };

  useEffect(() => {
    if (formInstance) {
      const staticAnswers = formInstance.fields.reduce(
        (acc, field) => {
          if (
            field.hidden &&
            field.type === FieldType.CHOICES &&
            field.choices?.length === 1
          ) {
            acc[field.key] = field.choices[0].value;
            return acc;
          }
          acc[field.key] = "";
          return acc;
        },
        {} as Record<string, string | number | boolean>,
      );

      setFormData({
        email: "",
        answers: staticAnswers,
      });
    }
  }, [formInstance]);

  if (formInstance?.fields.length === 0) {
    return (
      <div className="py-6 font-bold text-lg text-lightgrey animate-pulse">
        {t("wait")}
      </div>
    );
  }

  if (!formInstance) {
    return null;
  }

  return (
    <form method="POST" onSubmit={onFormSubmit}>
      <div>
        <h1 className="text-2xl font-black uppercase text-center mb-4">
          {t("form.title")}
        </h1>

        <p className="text-[16px] text-center text-blue text-sm mb-4">
          {t("form.subittle")}
        </p>

        <a
          href={getRulesAndRegulationsFileUrl(locale)}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-sm text-center text-blue underline mb-[30px]"
        >
          {t("rulesAndRegs")}
        </a>

        {formInstance.fields.map((field, index) => {
          const shouldRenderEmail = formInstance.email_field_position === index;
          const shouldRenderSubmit =
            formInstance.submit_field_position === index;

          return (
            <Fragment key={field.key}>
              {shouldRenderEmail && (
                <fieldset className="mt-[18px]">
                  <input
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        email: e.target.value.toLowerCase().trim(),
                      }))
                    }
                    name="email"
                    autoComplete="email"
                    type="email"
                    placeholder={`${t("email")}*`}
                    className={INPUT_CLASS}
                    required
                  />
                </fieldset>
              )}

              {!field.hidden && (
                <fieldset className="mt-[18px]">
                  {field.type === FieldType.DATE && (
                    <input
                      name={field.key}
                      type="date"
                      placeholder={`${field.label[locale]}${!field.optional ? "*" : ""}`}
                      className={INPUT_CLASS}
                      required={!field.optional}
                      value={(formData.answers[field.key] as string) || today}
                      onChange={(e) => onInputChange(e, field)}
                    />
                  )}

                  {field.type === FieldType.CHOICES && (
                    <Dropdown
                      name={field.key}
                      options={(field.choices || []) as Option[]}
                      required={!field.optional}
                      value={formData.answers[field.key] as string}
                      placeholder={`${field.label[locale]}${!field.optional ? "*" : ""}`}
                      autocomplete="off"
                      onChange={(value: string) =>
                        onInputChange(
                          {
                            target: { name: field.key, value },
                          } as unknown as React.ChangeEvent<HTMLInputElement>,
                          field,
                        )
                      }
                    />
                  )}

                  {field.type === FieldType.PILLS && (
                    <div className="border-b border-lightgrey pb-[30px]">
                      <label
                        htmlFor={field.key}
                        className="block text-lg font-medium mb-4"
                      >
                        {field.label[locale]}
                        {field.optional ? "" : "*"}
                      </label>
                      <InputPills
                        name={field.key}
                        options={(field.choices || []) as Option[]}
                        required={!field.optional}
                        value={formData.answers[field.key] as string}
                        onChange={(value: string) =>
                          onInputChange(
                            {
                              target: { name: field.key, value },
                            } as unknown as React.ChangeEvent<HTMLInputElement>,
                            field,
                          )
                        }
                      />
                    </div>
                  )}

                  {field.type === FieldType.TEXTAREA && (
                    <textarea
                      className={INPUT_CLASS}
                      name={field.key}
                      placeholder={`${field.label[locale]}${!field.optional ? "*" : ""}`}
                      style={{ height: "150px" }}
                      value={(formData.answers[field.key] as string) || ""}
                      onChange={(e) => onInputChange(e, field)}
                    />
                  )}

                  {field.type === FieldType.RATING && (
                    <div className="border-t border-b border-lightgrey pt-[30px] pb-[30px]">
                      <label
                        htmlFor={field.key}
                        className="block text-lg font-medium mb-4"
                      >
                        {field.label[locale]}
                      </label>

                      <div className="flex">
                        {RATINGS.map((rating) => (
                          <InputRadio
                            key={rating}
                            label={rating.toString()}
                            value={rating.toString()}
                            name={field.key}
                            checked={
                              formData.answers[field.key] === rating.toString()
                            }
                            className="mt-4 text-center grow flex font-bold text-blue flex-col items-center justify-center cursor-pointer"
                            inline
                            onChange={(value: string) =>
                              onInputChange(
                                {
                                  target: { name: field.key, value },
                                } as unknown as React.ChangeEvent<HTMLInputElement>,
                                field,
                              )
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {field.type === FieldType.YESNO && (
                    <>
                      <InputRadio
                        label={t("yes")}
                        value="yes"
                        name={field.key}
                        className="mt-4"
                        checked={formData.answers[field.key] === "true"}
                        onChange={(value: string) =>
                          onInputChange(
                            {
                              target: { name: field.key, value },
                            } as unknown as React.ChangeEvent<HTMLInputElement>,
                            field,
                          )
                        }
                      />
                      <InputRadio
                        label={t("no")}
                        value="no"
                        name={field.key}
                        className="mt-4"
                        checked={formData.answers[field.key] === "false"}
                        onChange={(value: string) =>
                          onInputChange(
                            {
                              target: { name: field.key, value },
                            } as unknown as React.ChangeEvent<HTMLInputElement>,
                            field,
                          )
                        }
                      />
                    </>
                  )}

                  {field.type === FieldType.POSTAL_CODE && (
                    <IMaskInput
                      mask={postalCodeMask}
                      unmask={true}
                      name={field.key}
                      required={!field.optional}
                      type="text"
                      placeholder={`${field.label[locale]}${!field.optional ? "*" : ""}`}
                      className={INPUT_CLASS}
                      value={(formData.answers[field.key] as string) || ""}
                      onAccept={(value) =>
                        onPostalCodeAccept(value as string, field)
                      }
                    />
                  )}

                  {field.type === FieldType.TEXT && (
                    <input
                      name={field.key}
                      type="text"
                      placeholder={`${field.label[locale]}${!field.optional ? "*" : ""}`}
                      className={INPUT_CLASS}
                      required={!field.optional}
                      value={(formData.answers[field.key] as string) || ""}
                      onChange={(e) => onInputChange(e, field)}
                    />
                  )}

                  {field.type === FieldType.PHONE && (
                    <input
                      name={field.key}
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      maxLength={10}
                      pattern="\d{10}"
                      title="Enter a 10-digit phone number"
                      placeholder={`${field.label[locale]}${!field.optional ? "*" : ""}`}
                      className={INPUT_CLASS}
                      required={!field.optional}
                      value={(formData.answers[field.key] as string) || ""}
                      onChange={(e) => onInputChange(e, field)}
                    />
                  )}

                  {field.type === FieldType.MARKETING && (
                    <label
                      htmlFor={field.key}
                      className="flex items-start gap-2"
                    >
                      <input
                        id={field.key}
                        name={field.key}
                        type="checkbox"
                        className="appearance-none"
                        checked={formData.answers[field.key] === "A"}
                        onChange={(e) =>
                          onInputChange(
                            e as unknown as React.ChangeEvent<HTMLInputElement>,
                            field,
                          )
                        }
                      />
                      <span className="block mt-1 min-w-[21px] min-h-[21px] border-[1px] border-black flex items-center justify-center">
                        {formData.answers[field.key] === "A" && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M13.3333 3.33333L5.33333 11.3333L2.66667 8.66667"
                              stroke="black"
                              strokeWidth="2"
                            />
                          </svg>
                        )}
                      </span>
                      <span
                        className="text-sm text-darkBlue [&_a]:text-blue [&_a]:underline"
                        dangerouslySetInnerHTML={{
                          __html: openLinksInNewTab(field.label?.[locale]),
                        }}
                      />
                    </label>
                  )}
                </fieldset>
              )}

              {shouldRenderSubmit && (
                <div className="text-center mt-[30px] pb-[10px]">
                  <Button
                    className="mx-auto"
                    type="submit"
                    loading={isFormSubmitting}
                  >
                    {t("submit")}
                  </Button>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
    </form>
  );
};

export default FormView;
